import React from "react";
import { Star, Sparkles, Flame, Award, Crown, Heart } from "lucide-react";

/**
 * Decorative badge overlay for package / theme cards.
 *
 * style options:
 *   - pill       : flat gold pill (default)
 *   - star       : ⭐ star icon + label (pill)
 *   - sparkle    : ✨ sparkle icon + label
 *   - fire       : 🔥 flame icon + label, red gradient
 *   - crown      : 👑 crown icon, gold
 *   - heart      : ❤︎ heart icon, soft pink
 *   - ribbon     : corner ribbon banner (diagonal)
 *   - stamp      : rotated stamp/sticker look
 *   - glow       : pulsing gold glow
 */
export default function Badge({ text, style = "pill" }) {
  if (!text || !text.trim()) return null;

  const ICONS = { star: Star, sparkle: Sparkles, fire: Flame, crown: Crown, heart: Heart, award: Award };
  const Icon = ICONS[style];

  if (style === "ribbon") {
    return (
      <div className="absolute top-0 right-0 z-10 overflow-hidden w-32 h-32 pointer-events-none" aria-hidden="false">
        <div className="absolute top-5 -right-10 w-44 bg-cozy-gold text-white text-[10px] font-bold tracking-widest uppercase text-center py-1.5 shadow-cozy rotate-45">
          {text}
        </div>
      </div>
    );
  }

  if (style === "stamp") {
    return (
      <div className="absolute top-4 right-4 z-10 -rotate-12 px-4 py-2 rounded-md border-2 border-red-600 text-red-600 bg-white/85 text-[11px] font-black tracking-widest uppercase shadow-cozy">
        {text}
      </div>
    );
  }

  if (style === "glow") {
    return (
      <div className="absolute top-4 right-4 z-10 px-3 py-1.5 rounded-full bg-cozy-gold text-white text-[10px] tracking-widest uppercase font-bold shadow-cozy badge-glow">
        {text}
      </div>
    );
  }

  if (style === "fire") {
    return (
      <div className="absolute top-4 right-4 z-10 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-white text-[10px] tracking-widest uppercase font-bold shadow-cozy"
           style={{ background: "linear-gradient(135deg,#E55A5A 0%,#D4A25A 100%)" }}>
        <Flame className="w-3 h-3" /> {text}
      </div>
    );
  }

  if (style === "heart") {
    return (
      <div className="absolute top-4 right-4 z-10 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-cozy-pink text-cozy-brown text-[10px] tracking-widest uppercase font-bold shadow-cozy">
        <Heart className="w-3 h-3 fill-current" /> {text}
      </div>
    );
  }

  if (Icon) {
    return (
      <div className="absolute top-4 right-4 z-10 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-cozy-gold text-white text-[10px] tracking-widest uppercase font-bold shadow-cozy">
        <Icon className="w-3 h-3" /> {text}
      </div>
    );
  }

  // default: pill
  return (
    <div className="absolute top-4 right-4 z-10 px-3 py-1.5 rounded-full bg-cozy-gold text-white text-[10px] tracking-widest uppercase font-bold shadow-cozy">
      {text}
    </div>
  );
}

export const BADGE_STYLES = [
  { value: "pill", label: "Gold Pill (default)" },
  { value: "star", label: "Star Icon" },
  { value: "sparkle", label: "Sparkle Icon" },
  { value: "fire", label: "Fire — Red/Gold gradient" },
  { value: "crown", label: "Crown Icon" },
  { value: "heart", label: "Heart — Pink" },
  { value: "award", label: "Award Ribbon Icon" },
  { value: "ribbon", label: "Corner Ribbon Banner" },
  { value: "stamp", label: "Stamp — Rotated red" },
  { value: "glow", label: "Glowing Gold (animated)" },
];
